/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"

import { headers, setupInterceptorsTo } from "./interceptors"
import { trackEvent } from "./awsRumClient"

const { VITE_BASE_URL } = import.meta.env

const API_ERROR_CODE_PATTERN = /^[A-Za-z0-9_.-]{1,64}$/

// Query string dropped and ID segments masked, so search text never reaches RUM.
export const toApiPath = (url = "") =>
  url
    .split("?")[0]
    .replace(/\/(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/gi, "/*")

// IDs the call was about (the page context can differ, e.g. background uploads).
// Case-scoped API paths use /txplans/:caseId; a few calls pass case_id/patient_id
// as a query string or axios `params` instead. Numeric IDs only.
export const getApiIds = (url = "", params?: unknown) => {
  const [path, query = ""] = url.split("?")
  const searchParams = new URLSearchParams(query)
  const configParams = (params ?? {}) as Record<string, unknown>
  const idAfter = (segment: string) => path.match(new RegExp(`/${segment}/(\\d+)(?=/|$)`))?.[1]
  const idParam = (key: string) =>
    [searchParams.get(key), configParams[key]]
      .map((value) => (value == null ? "" : String(value)))
      .find((value) => /^\d+$/.test(value))
  return {
    apiOrgId: idAfter("org") ?? "none",
    apiPatientId: idAfter("patients") ?? idParam("patient_id") ?? "none",
    apiCaseId: idAfter("txplans") ?? idParam("case_id") ?? "none",
  }
}

/**
 * Record a failed API call. Only method, masked path, status and the backend's
 * static error codes are sent; request/response bodies and error messages can
 * hold patient data and are never sent.
 */
export const trackApiError = (rejection: unknown) => {
  // The response interceptor rejects with the AxiosResponse (undefined on network errors).
  const response = (axios.isAxiosError(rejection) ? rejection.response : rejection) as
    | AxiosResponse<{ errors?: { error_code?: unknown }[] }>
    | undefined
  const config = response?.config ?? (axios.isAxiosError(rejection) ? rejection.config : undefined)
  const errors = response?.data?.errors
  const errorCodes = Array.isArray(errors)
    ? errors
        .map((error) => error?.error_code)
        .filter((code): code is string => typeof code === "string" && API_ERROR_CODE_PATTERN.test(code))
    : []
  trackEvent("api.error", {
    method: config?.method?.toUpperCase() ?? "unknown",
    path: config?.url ? toApiPath(config.url) : "unknown",
    ...getApiIds(config?.url, config?.params),
    status: response?.status ?? 0,
    errorCodes: errorCodes.join(",") || "none",
  })
}

class Http {
  private instance: AxiosInstance | null = null

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp()
  }

  initHttp() {
    const http = axios.create({
      baseURL: VITE_BASE_URL,
      headers,
      withCredentials: true,
    })

    setupInterceptorsTo(http)

    this.instance = http
    return http
  }

  private handleError = (error: any) => {
    // Sanitized api.error only; the raw rejection can carry backend payloads.
    trackApiError(error)
    throw error;
  }

  request<T = any, R = AxiosResponse<T>>(
    config: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.request(config)
  }

  get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.get<T, R>(url, config).catch((error) => this.handleError(error))
  }

  post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.post<T, R>(url, data, config).catch((error) => this.handleError(error))
  }

  put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.put<T, R>(url, data, config).catch((error) => this.handleError(error))
  }

  patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.patch<T, R>(url, data, config).catch((error) => this.handleError(error))
  }

  delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.delete<T, R>(url, config).catch((error) => this.handleError(error))
  }
}
// eslint-disable-next-line import/no-anonymous-default-export
export default new Http()
